import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { AxiosError } from 'axios';

export const API_V1 = '/api/private/visual-editor/v1';

export const entityTypes = [
  'macro',
  'model',
  'model_column',
  'source',
  'source_table',
  'source_table_column',
] as const;

export type EntityType = (typeof entityTypes)[number];

interface Branch {
  name?: string;
  commit_sha?: string;
  detail?: string;
}

export interface PullRequest {
  title: string;
  description: string;
  number: number;
  source_branch: string;
  target_branch: string;
  url: string;
  status: string;
}

export interface PrParams {
  projectId: number;
  resourceIdentifiers: string[];
  filePath: string;
  description: string;
  latestGitSha: string;
  entityType: EntityType;
}

export interface DescriptionChangesContext {
  branch?: Branch;
  pullRequest?: PullRequest;
  featureEnabled: boolean;
  isLoading: boolean;
  dbtCredsError: boolean;
  gitCredsError: boolean;
}

export const useBranch = (
  projectId: number,
  resourceIdentifier: string,
  featureEnabled: boolean,
) => {
  const enabled = !!projectId && !!resourceIdentifier && featureEnabled;
  return useQuery<Branch, AxiosError>({
    queryKey: ['branch', { projectId, resourceIdentifier }],
    queryFn: () => getBranch(projectId ?? 0, resourceIdentifier),
    enabled,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const getBranch = async (
  projectId: number,
  resourceIdentifier: string,
): Promise<Branch> => {
  const response = await axios.get<Branch>(
    `${API_V1}/explorer/description-changes/${projectId}/get-branch`,
    {
      params: {
        resource_identifier: resourceIdentifier,
      },
    },
  );

  return response.data;
};

export const usePr = (
  projectId: number,
  resourceIdentifiers: string[],
  featureEnabled: boolean,
) => {
  const enabled = !!projectId && !(resourceIdentifiers?.length === 0) && featureEnabled;

  return useQuery<PullRequest, AxiosError>({
    queryKey: ['pr', { projectId, resourceIdentifiers }],
    queryFn: () => getPr(projectId ?? 0, resourceIdentifiers),
    enabled,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * Returns a description change pull request for a resource, if one exists.
 * @param projectId - the project that the resource belongs to
 * @param resourceIdentifiers - a list of resource identifiers. should be in order of root > inner > ...
 * @returns PullRequest
 */
export const getPr = async (
  projectId: number,
  resourceIdentifiers: string[],
): Promise<PullRequest> => {
  const params = new URLSearchParams();
  resourceIdentifiers.forEach((resourceIdentifier) => {
    params.append('resource_identifiers', resourceIdentifier);
  });

  const response = await axios.get<PullRequest>(
    `${API_V1}/explorer/description-changes/${projectId}/get-pr`,
    {
      params: params,
    },
  );

  return response.data;
};

// This mutation is specific to the Description component
export const usePrMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postDescriptionPr,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          'branch',
          {
            projectId: variables.projectId,
            resourceIdentifiers: variables.resourceIdentifiers,
          },
        ],
      });
    },
    // TODO: understand when to use onError vs try catch in the component
    // onError: () => {},
  });
};

export const postDescriptionPr = async (newPr: PrParams): Promise<PullRequest> => {
  const response = await axios.post<PullRequest>(
    `${API_V1}/explorer/description-changes/${newPr.projectId}/create-pr`,
    {
      resource_identifiers: newPr.resourceIdentifiers,
      file_path: newPr.filePath,
      description: newPr.description,
      latest_commit_sha: newPr.latestGitSha,
      entity_type: newPr.entityType,
    },
  );
  return response.data;
};
